import { catalogFontStack, normalizeChatFontKey, preRangePinnedWeight } from 'lib/chatFonts'

export const CANVAS_W = 1920
export const CANVAS_H = 1080

export const ELEMENT_FONT_KEY = 'INTER' as const

export const FONT_FAMILY = catalogFontStack(ELEMENT_FONT_KEY)

export const elementFontStack = (fontKey: string | null | undefined): string => {
  if (fontKey == null) return FONT_FAMILY
  const normalized = normalizeChatFontKey(fontKey)
  if (normalized === 'DEFAULT') return FONT_FAMILY
  return catalogFontStack(normalized)
}

export const FW_REGULAR = 400
export const FW_MEDIUM = 400
export const FW_DEMI = 700
export const FW_BOLD = 800

export const elementPinnedWeight = (fontKey: string | null | undefined, weight: number): number => {
  const normalized = fontKey == null ? ELEMENT_FONT_KEY : normalizeChatFontKey(fontKey)
  return preRangePinnedWeight(normalized === 'DEFAULT' ? ELEMENT_FONT_KEY : normalized, weight)
}

export const POLL = {
  width: 575,
  heading: {
    paddingTop: 25,
    paddingBottom: 25,
    paddingLeft: 20,
    paddingRight: 20,
    fontSize: 44,
    lineHeight: 47,
    fontWeight: FW_MEDIUM
  },

  option: {
    paddingTop: 20,
    paddingBottom: 20,
    paddingLeft: 25,
    paddingRight: 25,
    chatCommandGap: 25,
    textMaxWidth: 360,
    textToBarGap: 15,
    barWidth: 450,
    barHeight: 10,
    fontSize: 26,
    lineHeight: 34,
    fontWeight: FW_MEDIUM
  },

  callToAction: {
    minHeight: 65,
    paddingTop: 10,
    paddingBottom: 10,
    textMaxWidth: 360,
    fontSize: 20,
    fontWeight: FW_MEDIUM
  },

  ticker: {
    width: 1920,
    minHeight: 116,

    left: {
      width: 440,
      paddingX: 30,
      paddingY: 18,
      eyebrow: {
        dotSize: 10,
        dotColor: '#FF3B30',
        gap: 10,
        fontSize: 15,
        lineHeight: 18,
        letterSpacing: '0.26em',
        fontWeight: FW_MEDIUM,
        marginBottom: 8
      },
      question: { fontSize: 34, lineHeight: 38 }
    },

    segment: {
      paddingX: 26,
      paddingY: 16,
      gap: 14,
      chip: {
        minWidth: 36,
        height: 34,
        paddingX: 9,
        radius: 6,
        fontSize: 20,
        fontWeight: FW_MEDIUM
      },
      title: {
        SMALL: { fontSize: 20, lineHeight: 26, fontWeight: FW_MEDIUM },
        MEDIUM: { fontSize: 26, lineHeight: 32, fontWeight: FW_MEDIUM },
        LARGE: { fontSize: 34, lineHeight: 40, fontWeight: FW_MEDIUM }
      },
      percent: { fontSize: 26, lineHeight: 32, fontWeight: FW_DEMI },
      bar: { height: 8, marginTop: 12 }
    },

    cta: {
      width: 380,
      paddingX: 24,
      paddingY: 14,
      fontSize: 24,
      lineHeight: 28,
      fontWeight: FW_MEDIUM,
      subtext: { fontSize: 17, lineHeight: 22, marginTop: 5, fontWeight: FW_MEDIUM, opacity: 0.85 }
    },

    blocks: {
      minPx: { question: 160, option: 150, cta: 120 },
      nudgeFraction: 0.01
    }
  }
} as const

export const TUG = {
  width: 1295,
  height: 175,
  imageSize: 175,
  barWidth: 875,
  barHeight: 80,
  imageBarGap: 30,
  pill: {
    width: 60,
    height: 40,
    fontSize: 22,
    fontWeight: FW_MEDIUM
  },

  barText: {
    fontSize: 36,
    lineHeight: 34,
    fontWeight: FW_MEDIUM
  },

  noImageText: {
    fontSize: 32,
    fontWeight: FW_MEDIUM
  }
} as const

export const SCOREBOARD = {
  width: 375,
  height: 85,

  title: {
    paddingTop: 10,
    fontSize: 18,
    fontWeight: FW_MEDIUM
  },
  divider: {
    top: 40,
    height: 32,
    horizontalGap: 10
  },
  score: {
    fontSize: 40,
    lineHeight: 46,
    fontWeight: FW_DEMI
  },
  name: {
    fontSize: 18,
    lineHeight: 46,
    fontWeight: FW_MEDIUM,
    scoreGap: 10
  }
} as const

export const GIVEAWAY = {
  width: 960,
  padding: 36,
  blockGap: 18,
  brand: {
    logoHeight: 48,
    fontSize: 26,
    lineHeight: 32,
    fontWeight: FW_DEMI,
    letterSpacing: '0.18em'
  },
  headline: { fontSize: 84, lineHeight: 90, fontWeight: FW_BOLD, letterSpacing: '0.04em' },
  subhead: { fontSize: 30, lineHeight: 36, fontWeight: FW_MEDIUM, letterSpacing: '0.08em' },
  body: { fontSize: 26, lineHeight: 32, fontWeight: FW_REGULAR },
  cta: { fontSize: 30, lineHeight: 36, fontWeight: FW_MEDIUM },
  count: { fontSize: 124, lineHeight: 124, fontWeight: FW_BOLD },
  countLabel: { fontSize: 26, lineHeight: 30, fontWeight: FW_DEMI, letterSpacing: '0.24em' },
  prizeImage: {
    size: 300,
    bannerSize: 220
  },
  accentStrip: { minHeight: 76, paddingY: 18, fontSize: 30, lineHeight: 36, fontWeight: FW_BOLD, letterSpacing: '0.16em' },
  winnerBox: {
    minWidth: 280,
    paddingY: 20,
    paddingX: 32,
    fontSize: 36,
    lineHeight: 42,
    fontWeight: FW_BOLD,
    letterSpacing: '0.04em',
    gap: 22,
    radius: 16
  },
  rules: { fontSize: 18, lineHeight: 24, fontWeight: FW_MEDIUM, paddingBottom: 12 },
  legal: { fontSize: 17, lineHeight: 23, fontWeight: FW_REGULAR, paddingY: 16 }
} as const
